import React, { useState, useEffect, useCallback } from "react";
import { X, Building2, PlusCircle } from "lucide-react";
import { useParams } from "react-router-dom";
import { useGetCompetitorsQuery } from "@/redux/feature/product/productApi";
import BrandWatchListIcon from "@/assets/icons/BrandWatchList.svg";
import { CustomButton } from "@/components/ui/Buttons";
import useCompetitorSocket from "@/hooks/competitorSocket";
import SafeImage from "@/components/ui/SafeImage";
import usePolling from "@/hooks/usePolling";

const POLLING_INTERVAL = 5000;

interface FilterState {
  selectedBrand: string | null;
}

interface CollectionFilteringProps {
  onBrandChange?: (brand: string | null) => void;
  handlerNavigateStructure: () => void;
  handlerOpenInsightsModal: () => void;
  setIsManualCompetitorModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CollectionFiltering({
  onBrandChange,
  setIsManualCompetitorModalOpen,
}: CollectionFilteringProps) {
  const { productId } = useParams();
  const [filters, setFilters] = useState<FilterState>({ selectedBrand: null });

  // 🔑 custom polling from your hook
  const { pollingInterval: hookPollingInterval } = usePolling({
    templateIds: ["manual_brand_competitor_fetch_completed"],
  });

  // 🔑 dynamic polling interval
  const [pollingInterval, setPollingInterval] = useState(0);

  const {
    data: brandsData = [],
    refetch: refetchBrandsData,
    isFetching,
    isLoading: isBrandsDataLoading,
  } = useGetCompetitorsQuery({ productId: productId! }, { pollingInterval });

  const brands = brandsData?.data ?? [];

  const isProcessing = brandsData?.meta?.isProcessing ?? false;

console.log(isProcessing);

  // 🔑 adjust polling based on processing state
  useEffect(() => {
    if (isProcessing) {
      setPollingInterval(5000); // keep hitting until false
    } else {
      setPollingInterval(hookPollingInterval || 0);
      // fallback: if hookPollingInterval > 0, use that
    }
  }, [isProcessing, hookPollingInterval]);

  const toggleBrandSelection = useCallback(
    (brand: string) => {
      setFilters((prev) => {
        const newBrand = prev.selectedBrand === brand ? null : brand;
        onBrandChange?.(newBrand);
        return { selectedBrand: newBrand };
      });
    },
    [onBrandChange]
  );

  return (
    <div className="w-full bg-white border rounded-lg border-[#E4E7EC] p-4 sm:p-5">
      <BrandCompetitorHeader />

      <CustomButton
        onClick={() => setIsManualCompetitorModalOpen(true)}
        fontWeight="600"
        padding="8px 12px"
        variant="outline"
        color="#0D0D0D"
        border="0px"
        className="flex gap-x-2 sm:gap-x-3 items-center my-4 w-full sm:w-auto whitespace-nowrap"
      >
        <span>
          <PlusCircle className="text-[#000000] w-5 h-5 sm:w-6 sm:h-6" />
        </span>
        <span className="text-sm 2xl:text-base">Add a New Brand</span>
      </CustomButton>

      <div className="mt-4 space-y-6">
        {filters.selectedBrand && (
          <SelectedTag
            label={filters.selectedBrand}
            onRemove={() => toggleBrandSelection(filters.selectedBrand!)}
          />
        )}

        <BrandList
          brands={brands}
          selectedBrand={filters.selectedBrand}
          onSelectBrand={toggleBrandSelection}
          isLoading={isProcessing || isBrandsDataLoading}
        />
      </div>
    </div>
  );
}

export function BrandCompetitorHeader() {
  return (
    <div className="flex items-start sm:items-center gap-x-3 border-b border-[#F2F4F7] pb-4">
      <BrandWatchListIcon />
      <div>
        <h6 className="text-[#0D0D0D] text-sm sm:text-base font-semibold">
          Brand WatchList
        </h6>
        <p className="text-[#A7A7A7] text-xs">
          Select at least{" "}
          <span className="text-[#424242] font-medium">10 Brands</span> to keep
          watch
        </p>
      </div>
    </div>
  );
}

export function BrandList({
  brands,
  selectedBrand,
  onSelectBrand,
  isLoading,
}: {
  brands: { name: string; profile_picture_url: string }[];
  selectedBrand: string | null;
  onSelectBrand?: (brand: string) => void | undefined;
  isLoading: boolean;
}) {
  if (isLoading && brands.length === 0) {
    return <SkeletonBrandList count={5} />;
  }

  if (!isLoading && brands.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Building2 className="w-8 h-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">No brands found yet</p>
        <p className="text-xs text-gray-400">
          Try adding a new brand to start your watchlist.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[50vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
      {brands?.map((brand) => (
        <BrandItem
          key={brand.name}
          brand={brand}
          isSelected={selectedBrand === brand.name}
          onSelect={onSelectBrand}
        />
      ))}
      {isLoading && brands.length > 0 && <SkeletonBrandList count={2} />}{" "}
      {/* subtle loading indicator */}
    </div>
  );
}

export function BrandItem({
  brand,
  isSelected,
  onSelect,
}: {
  brand: { name: string; profile_picture_url: string };
  isSelected: boolean;
  onSelect?: (brand: string) => void;
}) {
  return (
    <div
      onClick={() => onSelect?.(brand.name)}
      className={`rounded-lg p-3 flex items-center gap-x-3 cursor-pointer border transition-all ${
        isSelected
          ? "border-gray-400 bg-gray-50"
          : "border-transparent hover:bg-gray-50 hover:border-gray-300"
      }`}
    >
      <div
        className={`rounded-full size-3 2xl:size-4 border-2 flex-shrink-0 ${
          isSelected ? "border-black bg-black" : "border-gray-400"
        }`}
      >
        {isSelected && (
          <div className="w-full h-full rounded-full bg-white scale-50" />
        )}
      </div>

      <div className="2xl:w-10 2xl:h-10 size-7 rounded-full bg-gray-300 flex items-center justify-center">
        {brand.profile_picture_url ? (
          <SafeImage
            src={brand.profile_picture_url}
            alt={`${brand.name} logo`}
            className="2xl:w-10 2xl:h-10 size-7 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-100"
            fallbackComponent={
              <div className="2xl:w-10 2xl:h-10 size-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex-shrink-0 ring-2 ring-gray-100 flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {brand.name?.charAt(0)?.toUpperCase() || "?"}
                </span>
              </div>
            }
          />
        ) : (
          <div className="2xl:w-10 2xl:h-10 size-7 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0 ring-2 ring-gray-100" />
        )}
      </div>

      <h3 className="flex-1 text-[#0D0D0D] font-medium text-[13px] 2xl:text-base truncate">
        {brand.name}
      </h3>

      {isSelected && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(brand.name);
          }}
          className="bg-[#EEF0F2] rounded-lg w-7 h-7 flex items-center justify-center hover:bg-gray-300"
        >
          <X className="text-[#53585F] w-5 h-5" />
        </button>
      )}
    </div>
  );
}

export function SelectedTag({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <span className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-gray-100 text-gray-800 rounded-full border">
        <span className="truncate">{label}</span>
        <button
          onClick={onRemove}
          className="ml-2 hover:bg-gray-200 rounded-full p-0.5"
        >
          <X className="w-3 h-3 sm:w-4 sm:h-4" />
        </button>
      </span>
    </div>
  );
}

export function SkeletonBrandList({ count }: { count: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg p-3 flex items-center gap-x-3 border border-gray-200 animate-pulse"
        >
          {/* Radio Circle */}
          <div className="rounded-full w-4 h-4 border-2 border-gray-300"></div>

          {/* Profile Picture */}
          <div className="2xl:w-10 2xl:h-10 size-7 rounded-full bg-gray-300" />

          {/* Brand Name */}
          <div className="flex-1 h-4 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  );
}
